import { agreementPackages, minProductionsOf, PROJECT_DEFAULTS, QUOTE_TERMS, type PriceListData, type QuoteTerms } from './pricing.js';
import { cleanTravelRule, isStandardTravel, sameTravel, STANDARD_TRAVEL, type TravelRule } from './travel.js';

/**
 * The client's agreement as figures (owner 2026-10-29: "alla detaljer från kundens avtal ska ligga som kvantifierbar data
 * under kunden och avtalet … räknas ut från det avtalet vi har med den kunden default. Sen kan vi justera"). An agreement
 * signed in the Hub has them in its quote; an older agreement (Monday's PDFs) is read by Klara, and a person approves
 * the figures before they are used ("Aktiva kunder först, med granskning"). Missing figures fall back to the standard:
 * the price list for prices, the quote defaults and the general terms for the rest.
 */
export interface AgreementPackagePrice {
  key: string;
  price: number;
}

export interface AgreementCosts {
  /** the package (price list key) and its price per month or production */
  packageKey: string | null;
  price: number | null;
  /**
   * Every package the agreement lists with its price (owner 2026-10-30: "alla avtal behöver innehålla samtliga paket och
   * priser"): per month for a subscription, the client switching between them, or per production when calling off
   */
  packages: AgreementPackagePrice[] | null;
  startupFee: number | null;
  /**
   * Per production: the minimum number of productions within the period (0 = a frame agreement with no minimum, owner
   * 2026-10-29); null for a subscription or when the agreement does not say
   */
  minProductions: number | null;
  /** binding period (subscription) or the minimum's period (per production), and notice, months */
  periodMonths: number | null;
  /** the minimum's last day when the agreement gives a date instead of months ("fram till och med 2027-09-09") */
  untilDate: string | null;
  noticeMonths: number | null;
  paymentDays: number | null;
  /**
   * Drafts (client rounds) included per video (owner 2026-10-31: "det ska plockas från avtalet vad som ingår, sen kan det
   * tillkomma flera rundor"); the price list says 2
   */
  drafts: number | null;
  /** extra shoot, editing or admin time, kr/h */
  hourlyRate: number | null;
  /** work the client asks for outside weekdays 08–17: times the hourly price */
  overtimeFactor: number | null;
  rescheduleFee: number | null;
  /** cancelling a production: % of its price, early (cancelDays or more before) and late */
  cancelEarlyPct: number | null;
  cancelLatePct: number | null;
  cancelDays: number | null;
  expressPrice: number | null;
  /**
   * Working days from the shoot to the first version (owner 2026-11-01: the delivery time comes from the client's
   * agreement; older agreements often promise longer than the Hub's five)
   */
  firstDraftDays: number | null;
  dunderPrice: number | null;
  /** actor or voice-over, "from" price */
  talentPrice: number | null;
  travel: TravelRule | null;
  /** what else the agreement says that costs or saves money but is not a figure above */
  other: string | null;
}

export const COST_KEYS = [
  'packageKey', 'price', 'packages', 'startupFee', 'minProductions', 'periodMonths', 'untilDate', 'noticeMonths', 'paymentDays', 'drafts', 'hourlyRate', 'overtimeFactor', 'rescheduleFee',
  'cancelEarlyPct', 'cancelLatePct', 'cancelDays', 'expressPrice', 'dunderPrice', 'talentPrice', 'travel', 'other', 'firstDraftDays'
] as const satisfies readonly (keyof AgreementCosts)[];
export type CostKey = (typeof COST_KEYS)[number];
/** the figures (not the package, the travel rule or the free text) */
export const NUMBER_KEYS = COST_KEYS.filter((k): k is Exclude<CostKey, 'packageKey' | 'packages' | 'untilDate' | 'travel' | 'other'> => k !== 'packageKey' && k !== 'packages' && k !== 'untilDate' && k !== 'travel' && k !== 'other');
type NumberKey = (typeof NUMBER_KEYS)[number];

export const COST_LABELS: Record<CostKey, { sv: string; en: string; unit: 'kr' | 'kr/h' | '%' | 'months' | 'days' | 'x' | null }> = {
  packageKey: { sv: 'Paket', en: 'Package', unit: null },
  price: { sv: 'Pris (per månad eller produktion)', en: 'Price (per month or production)', unit: 'kr' },
  packages: { sv: 'Alla paket i avtalet', en: 'All packages in the agreement', unit: null },
  startupFee: { sv: 'Startavgift', en: 'Start-up fee', unit: 'kr' },
  minProductions: { sv: 'Minsta antal produktioner (0 = ramavtal utan minimum)', en: 'Minimum productions (0 = frame agreement, no minimum)', unit: null },
  periodMonths: { sv: 'Bindningstid eller period för minimiantalet', en: 'Binding period or period of the minimum', unit: 'months' },
  untilDate: { sv: 'Gäller till', en: 'Runs until', unit: null },
  noticeMonths: { sv: 'Uppsägningstid', en: 'Notice', unit: 'months' },
  paymentDays: { sv: 'Betalningsvillkor', en: 'Payment terms', unit: 'days' },
  drafts: { sv: 'Utkast som ingår per video', en: 'Drafts included per video', unit: null },
  hourlyRate: { sv: 'Timpris för extra tid', en: 'Hourly price for extra time', unit: 'kr/h' },
  overtimeFactor: { sv: 'Övertid (kvällar och helger), gånger timpriset', en: 'Overtime (evenings, weekends), times the hourly price', unit: 'x' },
  rescheduleFee: { sv: 'Ombokningsavgift', en: 'Rescheduling fee', unit: 'kr' },
  cancelEarlyPct: { sv: 'Avbokning i god tid', en: 'Cancelling in good time', unit: '%' },
  cancelLatePct: { sv: 'Sen avbokning', en: 'Late cancelling', unit: '%' },
  cancelDays: { sv: 'Gräns för sen avbokning', en: 'Late cancelling limit', unit: 'days' },
  expressPrice: { sv: 'Expressleverans', en: 'Express delivery', unit: 'kr' },
  firstDraftDays: { sv: 'Leveranstid, första version (arbetsdagar efter inspelning)', en: 'Delivery time, first version (working days after the shoot)', unit: 'days' },
  dunderPrice: { sv: 'Dunderleverans', en: 'Dunderleverans', unit: 'kr' },
  talentPrice: { sv: 'Skådespelare eller speaker (från)', en: 'Actor or voice-over (from)', unit: 'kr' },
  travel: { sv: 'Resor', en: 'Travel', unit: null },
  other: { sv: 'Övriga villkor som kostar', en: 'Other terms that cost', unit: null }
};

export const emptyCosts = (): AgreementCosts => ({
  packageKey: null, price: null, packages: null, startupFee: null, minProductions: null, periodMonths: null, untilDate: null, noticeMonths: null, paymentDays: null, drafts: null, hourlyRate: null, overtimeFactor: null,
  rescheduleFee: null, cancelEarlyPct: null, cancelLatePct: null, cancelDays: null, expressPrice: null, firstDraftDays: null, dunderPrice: null, talentPrice: null, travel: null, other: null
});

const LIMITS: Record<NumberKey, number> = {
  price: 10_000_000, startupFee: 1_000_000, minProductions: 500, periodMonths: 120, noticeMonths: 36, paymentDays: 120, drafts: 20, hourlyRate: 20_000, overtimeFactor: 5,
  rescheduleFee: 1_000_000, cancelEarlyPct: 100, cancelLatePct: 100, cancelDays: 365, expressPrice: 1_000_000, firstDraftDays: 60, dunderPrice: 1_000_000, talentPrice: 1_000_000
};

/** Any object into figures: numbers in range (else null), the package from the price list, text capped. */
export function cleanCosts(raw: unknown, pl?: PriceListData | null): AgreementCosts {
  const o = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const c = emptyCosts();
  for (const k of NUMBER_KEYS) {
    const v = o[k];
    if (v === null || v === undefined || v === '') continue;
    const n = Number(typeof v === 'string' ? v.replace(/\s/g, '').replace(',', '.') : v);
    if (Number.isFinite(n) && n >= 0 && n <= LIMITS[k]) c[k] = k === 'overtimeFactor' ? Math.round(n * 100) / 100 : Math.round(n);
  }
  if (typeof o.packageKey === 'string' && o.packageKey.trim()) c.packageKey = pl?.packages.find((x) => x.key.toLowerCase() === (o.packageKey as string).trim().toLowerCase())?.key || o.packageKey.trim().slice(0, 40);
  if (o.travel && typeof o.travel === 'object') c.travel = cleanTravelRule(o.travel);
  if (Array.isArray(o.packages)) {
    const seen = new Set<string>();
    const list: AgreementPackagePrice[] = [];
    for (const x of o.packages.slice(0, 20)) {
      const r = (x && typeof x === 'object' ? x : {}) as Record<string, unknown>;
      const name = typeof r.key === 'string' ? r.key.trim() : '';
      const n = Number(typeof r.price === 'string' ? r.price.replace(/\s/g, '').replace(',', '.') : r.price);
      if (!name || !Number.isFinite(n) || n <= 0 || n > LIMITS.price) continue;
      const key = pl?.packages.find((p) => p.key.toLowerCase() === name.toLowerCase())?.key || name.slice(0, 40);
      if (seen.has(key)) continue;
      seen.add(key);
      list.push({ key, price: Math.round(n) });
    }
    if (list.length) c.packages = list;
  }
  if (typeof o.untilDate === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(o.untilDate.trim()) && !Number.isNaN(Date.parse(o.untilDate.trim()))) c.untilDate = o.untilDate.trim();
  if (typeof o.other === 'string' && o.other.trim()) c.other = o.other.trim().slice(0, 2000);
  return c;
}

/** A Hub quote's terms as figures: nothing to read or approve, the quote is the agreement. */
export function costsFromQuote(t: QuoteTerms, pl: PriceListData): AgreementCosts {
  const opt = (k: string) => t.addOns.find((a) => a.key === k)?.price ?? null;
  return {
    ...emptyCosts(),
    packageKey: t.packageKey,
    price: t.price,
    packages: agreementPackages(pl, t).map((x) => ({ key: x.key, price: x.price })),
    startupFee: t.startupFee,
    minProductions: t.kind === 'project' ? minProductionsOf(t) : null,
    periodMonths: t.kind === 'project' ? (t.periodMonths ?? PROJECT_DEFAULTS.periodMonths) : t.periodMonths,
    noticeMonths: t.kind === 'subscription' ? QUOTE_TERMS.noticeMonths : null,
    paymentDays: QUOTE_TERMS.paymentDays,
    hourlyRate: opt('hour') ?? pl.addOns.find((a) => a.key === 'hour')?.price ?? null,
    overtimeFactor: 2,
    rescheduleFee: opt('reschedule'),
    cancelEarlyPct: t.kind === 'project' ? QUOTE_TERMS.cancelEarlyPct : null,
    cancelLatePct: t.kind === 'project' ? QUOTE_TERMS.cancelLatePct : null,
    cancelDays: t.kind === 'project' ? QUOTE_TERMS.cancelDays : null,
    expressPrice: opt('express'),
    // the Hub quote's own words: "första version levereras inom fem (5) arbetsdagar efter inspelningen"
    firstDraftDays: 5,
    dunderPrice: opt('dunder'),
    talentPrice: opt('talent'),
    travel: t.travel ?? null,
    other: t.specialTerms ?? null
  };
}

/** Drafts per video when the agreement says nothing (the price list: "2 drafts, musik, undertexter och upp till 3 format"). */
export const STANDARD_DRAFTS = 2;

/** What applies when the agreement says nothing: the price list's add-ons, the quote defaults, the general terms. */
export function standardCosts(pl: PriceListData): AgreementCosts {
  const opt = (k: string) => pl.addOns.find((a) => a.key === k)?.price ?? null;
  return {
    ...emptyCosts(),
    // the list's monthly prices: what a subscription's packages are compared with (a project agreement's are its tier's)
    packages: pl.packages.filter((p) => !p.custom && p.monthly).map((p) => ({ key: p.key, price: p.monthly! })),
    noticeMonths: QUOTE_TERMS.noticeMonths,
    paymentDays: QUOTE_TERMS.paymentDays,
    drafts: STANDARD_DRAFTS,
    hourlyRate: opt('hour'),
    overtimeFactor: 2,
    rescheduleFee: opt('reschedule'),
    cancelEarlyPct: QUOTE_TERMS.cancelEarlyPct,
    cancelLatePct: QUOTE_TERMS.cancelLatePct,
    cancelDays: QUOTE_TERMS.cancelDays,
    expressPrice: opt('express'),
    firstDraftDays: 5,
    dunderPrice: opt('dunder'),
    talentPrice: opt('talent'),
    travel: { ...STANDARD_TRAVEL }
  };
}

export type CostSource = 'agreement' | 'standard';
/** The agreement's figures with the standard where it says nothing, and where each one came from. */
export function effectiveCosts(agreement: AgreementCosts | null, pl: PriceListData): { costs: AgreementCosts; from: Record<CostKey, CostSource> } {
  const std = standardCosts(pl);
  const costs = { ...std };
  const from = Object.fromEntries(COST_KEYS.map((k) => [k, 'standard'])) as Record<CostKey, CostSource>;
  for (const k of COST_KEYS) {
    const v = agreement?.[k];
    if (v === null || v === undefined || (k === 'travel' && isStandardTravel(v as TravelRule))) continue;
    (costs as Record<string, unknown>)[k] = v;
    from[k] = 'agreement';
  }
  return { costs, from };
}

// ---- reading an agreement PDF with Klara ----

/** The instruction Klara gets with the PDF. Swedish agreements; the answer is JSON only. */
export const TERMS_PROMPT = `Du läser ett kundavtal mellan Klara Färdiga Film (KFF, Färdiga Film Sweden AB) och en kund. Ta ut de siffror som påverkar vad kunden ska betala.
Svara ENDAST med ett JSON-objekt, utan förklaringar och utan kodstaket, med exakt dessa nycklar:
{
  "summary": "en mening om vad avtalet gäller",
  "values": {
    "packageKey": paketnamn (Bergman, Spielberg, Hitchcock, Kubrick, Scott, Lucas, Jones, Akerman) eller null,
    "price": pris i kronor exkl. moms per månad (abonnemang) eller per produktion, eller null,
    "packages": ALLA paket som avtalet listar med sitt pris i kronor exkl. moms (per månad för abonnemang, per produktion för avrop), som [{"key": paketnamn, "price": belopp}], eller null om avtalet bara nämner ett paket,
    "startupFee": startavgift i kronor eller null,
    "minProductions": för avtal per produktion (avrop): minsta antal produktioner kunden åtagit sig inom perioden, 0 om det är ett ramavtal utan minimiåtagande, null för abonnemang eller om avtalet inte säger något,
    "periodMonths": bindningstid/avtalsperiod i månader, eller perioden som minimiantalet gäller för, eller null,
    "untilDate": om minimiåtagandet eller avtalet gäller till ett visst datum i stället för ett antal månader: datumet som ÅÅÅÅ-MM-DD, annars null,
    "noticeMonths": uppsägningstid i månader eller null,
    "paymentDays": betalningsvillkor i dagar eller null,
    "drafts": hur många utkast (drafts, feedbackrundor) som ingår per video, t.ex. 2, eller null,
    "hourlyRate": timpris i kronor för extra tid (inspelning, redigering, admin, tilläggsarbete) eller null,
    "overtimeFactor": hur många gånger timpriset arbete på kvällar/helger kostar (t.ex. 2) eller null,
    "rescheduleFee": ombokningsavgift i kronor eller null,
    "cancelEarlyPct": procent av priset vid avbokning i god tid eller null,
    "cancelLatePct": procent av priset vid sen avbokning eller null,
    "cancelDays": antal dagar före inspelning som skiljer god tid från sen avbokning eller null,
    "expressPrice": pris för expressleverans i kronor eller null,
    "firstDraftDays": hur många arbetsdagar efter inspelningen (eller efter att materialet finns) KFF har på sig att leverera första versionen, som heltal, eller null,
    "dunderPrice": pris för dunderleverans i kronor eller null,
    "talentPrice": pris (från) för skådespelare eller speaker i kronor eller null,
    "travel": null om avtalet inte säger något om resor, annars {"included": "stockholm" (resor inom Stockholm ingår, övriga debiteras) | "all" (alla resor ingår) | "none" (alla resor debiteras), "timeShare": andel av timpriset för restid (0.5 = halva timpriset, 0 = restid ingår), "hourlyRate": eget timpris för restid i kronor eller null},
    "other": övriga villkor som kostar eller sparar pengar och inte passar ovan, kort på svenska, eller null
  },
  "quotes": { samma nycklar som i values: den exakta meningen ur avtalet som värdet kommer från, högst 200 tecken; utelämna nycklar som är null }
}
Gissa aldrig: om avtalet inte anger ett värde, sätt null. Belopp i hela kronor utan mellanslag.`;

/** Only the delivery time, for agreements read before it was asked for (owner 2026-11-01). */
export const DELIVERY_PROMPT = `Du läser ett kundavtal mellan Klara Färdiga Film (KFF) och en kund. Hur många arbetsdagar efter inspelningen (eller efter att materialet finns) har KFF på sig att leverera första versionen av filmen?
Svara ENDAST med JSON, utan kodstaket: {"firstDraftDays": heltal eller null, "quote": "den exakta meningen ur avtalet, högst 200 tecken, eller null"}
Gissa aldrig: står det inte i avtalet, svara null. Räkna om veckor till arbetsdagar (1 vecka = 5).`;

export function parseDeliveryReading(text: string): { days: number | null; quote: string | null } {
  const start = text.indexOf('{');
  const end = text.lastIndexOf('}');
  if (start < 0 || end <= start) return { days: null, quote: null };
  try {
    const o = JSON.parse(text.slice(start, end + 1)) as Record<string, unknown>;
    const n = Number(o.firstDraftDays);
    const days = o.firstDraftDays !== null && Number.isInteger(n) && n >= 1 && n <= LIMITS.firstDraftDays ? n : null;
    const quote = days && typeof o.quote === 'string' && o.quote.trim() ? o.quote.trim().slice(0, 300) : null;
    return { days, quote };
  } catch {
    return { days: null, quote: null };
  }
}

export interface TermsReading {
  summary: string | null;
  costs: AgreementCosts;
  quotes: Partial<Record<CostKey, string>>;
}

/** Klara's answer into figures and quotes; null when it is not the JSON asked for. */
export function parseTermsReading(text: string, pl?: PriceListData | null): TermsReading | null {
  const start = text.indexOf('{');
  const end = text.lastIndexOf('}');
  if (start < 0 || end <= start) return null;
  let o: Record<string, unknown>;
  try {
    o = JSON.parse(text.slice(start, end + 1)) as Record<string, unknown>;
  } catch {
    return null;
  }
  if (!o.values || typeof o.values !== 'object') return null;
  const costs = cleanCosts(o.values, pl);
  const quotes: Partial<Record<CostKey, string>> = {};
  const q = (o.quotes && typeof o.quotes === 'object' ? o.quotes : {}) as Record<string, unknown>;
  for (const k of COST_KEYS) if (typeof q[k] === 'string' && (q[k] as string).trim() && costs[k] !== null) quotes[k] = (q[k] as string).trim().slice(0, 300);
  return { summary: typeof o.summary === 'string' ? o.summary.trim().slice(0, 300) || null : null, costs, quotes };
}

/** How many figures a reading found (the review list shows it). */
export const foundCount = (c: AgreementCosts) => COST_KEYS.filter((k) => c[k] !== null).length;

// ---- how the figures are shown (owner 2026-10-30: "enklast möjliga för användaren så de förstår") ----

/** The three questions a producer asks of an agreement, and the rest; the review and the client card use the same groups. */
export const COST_GROUPS: { key: 'pays' | 'promised' | 'delivery' | 'extra' | 'other'; sv: string; en: string; keys: CostKey[] }[] = [
  { key: 'pays', sv: 'Kunden betalar', en: 'The client pays', keys: ['packageKey', 'price', 'packages', 'startupFee'] },
  { key: 'promised', sv: 'Kunden har lovat', en: 'The client has committed to', keys: ['minProductions', 'periodMonths', 'untilDate', 'noticeMonths', 'paymentDays', 'drafts'] },
  { key: 'delivery', sv: 'KFF levererar', en: 'KFF delivers', keys: ['firstDraftDays'] },
  { key: 'extra', sv: 'Det här kostar extra', en: 'What costs extra', keys: ['hourlyRate', 'overtimeFactor', 'travel', 'rescheduleFee', 'expressPrice', 'dunderPrice', 'talentPrice', 'cancelEarlyPct', 'cancelLatePct', 'cancelDays'] },
  { key: 'other', sv: 'Övrigt', en: 'Other', keys: ['other'] }
];

/**
 * A figure against the standard: not in the agreement (the standard applies), the same as the standard, different from it,
 * or found with nothing to compare (the package, its price, the minimum and the dates are the agreement's own).
 */
export type CostStatus = 'missing' | 'standard' | 'differs' | 'found';
export function costStatus(k: CostKey, c: AgreementCosts, std: AgreementCosts): CostStatus {
  const v = c[k];
  if (v === null || v === undefined) return 'missing';
  const s = std[k];
  if (s === null || s === undefined) return 'found';
  if (k === 'packages') {
    // a project agreement's packages are priced at its tier: nothing on the monthly list to compare with
    if (c.minProductions !== null) return 'found';
    const list = new Map((s as AgreementPackagePrice[]).map((x) => [x.key, x.price]));
    return (v as AgreementPackagePrice[]).every((x) => list.get(x.key) === x.price) ? 'standard' : 'differs';
  }
  if (k === 'travel') return sameTravel({ ...(v as TravelRule), note: null }, { ...(s as TravelRule), note: null }) ? 'standard' : 'differs';
  return v === s ? 'standard' : 'differs';
}
